import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'

interface ServiceDetail {
  id: string
  client_id: number
  client_name: string
  date: string
  service_type: string
  status: string
  priority: string
  related_issue_id?: string
  location?: string
  street_address: string
  latitude?: string
  longitude?: string
  description?: string
  contact_person_name?: string
  contact_phone?: string
  additional_tools?: string
  planned_date?: string
  completion_comment?: string
  additional_materials?: string
  author_name?: string
  created_at?: string
  images?: Array<{
    id: number
    filename: string
    original_filename: string
    file_path: string
    file_size: number
    mime_type: string
    description?: string
    uploaded_at: string
  }>
  status_history?: Array<{
    id: number
    old_status?: string
    new_status: string
    changed_by_name: string
    changed_at: string
    planning_date?: string
    completion_comment?: string
  }>
}

interface ServicesDisplayPageProps { token: string | null }

export default function ServicesDisplayPage({ token }: ServicesDisplayPageProps) {
  const [services, setServices] = useState<ServiceDetail[]>([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(true)
  const [timeLeft, setTimeLeft] = useState(30)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [kioskToken, setKioskToken] = useState<string | null>(() => localStorage.getItem('kiosk_token'))
  const [isKioskMode, setIsKioskMode] = useState(false)
  const [pairingRequired, setPairingRequired] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search)
    return !!params.get('pairing_code')
  })
  const [pairingFailed, setPairingFailed] = useState(false)

  const getAuthHeaders = (): Record<string, string> => {
    const activeToken = kioskToken || token
    if (!activeToken) return {}
    return { 'Authorization': `Bearer ${activeToken}` }
  }

  const hasAnyAuth = !!(kioskToken || (!pairingRequired && token))

  useEffect(() => {
    const runPairing = async () => {
      const params = new URLSearchParams(window.location.search)
      const pairingCode = params.get('pairing_code')
      if (!pairingCode) {
        setPairingRequired(false)
        return
      }

      try {
        setLoading(true)
        setPairingFailed(false)
        const response = await fetch('/api/kiosk/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            pairing_code: pairingCode,
            device_identifier: navigator.userAgent,
            device_name: 'Kiosk services-display'
          })
        })

        if (!response.ok) {
          const err = await response.json()
          setError(err.detail || 'Nie udało się sparować urządzenia')
          setPairingFailed(true)
          return
        }

        const data = await response.json()
        localStorage.setItem('kiosk_token', data.kiosk_token)
        setKioskToken(data.kiosk_token)
        setIsKioskMode(true)
        setPairingRequired(false)
        setPairingFailed(false)

        // Usuń pairing_code z URL po udanym sparowaniu
        window.history.replaceState({}, document.title, '/services-display')
      } catch (e) {
        setError('Błąd parowania urządzenia kiosk')
        setPairingFailed(true)
      } finally {
        setLoading(false)
      }
    }

    runPairing()
  }, [token])

  useEffect(() => {
    if (pairingRequired && !kioskToken) {
      setLoading(false)
      return
    }

    if (!hasAnyAuth) {
      setLoading(false)
      return
    }

    loadServices()

    // Odśwież dane co 20 sekund
    const refreshInterval = setInterval(() => {
      loadServices()
    }, 20000)

    return () => clearInterval(refreshInterval)
  }, [kioskToken, token, pairingRequired])

  useEffect(() => {
    if (!services.length || !isPlaying) return

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Przełącz na następny serwis
          setCurrentIndex(prevIndex => (prevIndex + 1) % services.length)
          return 30
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [services.length, isPlaying])

  // Mark current service as seen when it becomes visible
  useEffect(() => {
    if (services.length === 0) {
      setCurrentIndex(0)
      return
    }
    if (currentIndex >= services.length) {
      setCurrentIndex(0)
    }
  }, [services.length, currentIndex])

  useEffect(() => {
    if (kioskToken) return
    if (services.length > 0 && services[currentIndex]) {
      markServiceAsSeen(services[currentIndex].id)
    }
  }, [currentIndex, services, kioskToken])

  const loadServices = async () => {
    try {
      setLoading(true)
      setError('')

      if (kioskToken) {
        const response = await fetch('/api/kiosk/services-feed', {
          headers: getAuthHeaders(),
        })

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('kiosk_token')
            setKioskToken(null)
          }
          setError('Błąd podczas ładowania danych kiosk')
          return
        }

        const feedData: Array<ServiceDetail> = await response.json()
        setServices(feedData.filter(s => s.status !== 'zrealizowane'))
        setLoading(false)
        return
      }

      const params = new URLSearchParams({
        sort_by: 'priority',
        sort_order: 'DESC'
      })

      const response = await fetch(`/api/services?${params}`, {
        headers: getAuthHeaders(),
      })

      if (!response.ok) {
        setError('Błąd podczas ładowania danych')
        return
      }

      const basicServices: Array<ServiceDetail> = await response.json()

      if (basicServices.length === 0) {
        setServices([])
        setLoading(false)
        return
      }

      // Pobierz szczegóły dla wszystkich serwisów
      const detailedServices: ServiceDetail[] = []
      for (const service of basicServices) {
        try {
          const detailResponse = await fetch(`/api/services/${service.id}`, {
            headers: getAuthHeaders(),
          })
          if (detailResponse.ok) {
            const detail = await detailResponse.json()
            detailedServices.push(detail)
          }
        } catch (err) {
          console.error(`Error loading service ${service.id}:`, err)
        }
      }

      setServices(detailedServices.filter(s => s.status !== 'zrealizowane'))


    } catch (err) {
      setError('Błąd połączenia z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const markServiceAsSeen = async (serviceId: string) => {
    try {
      await fetch(`/api/services/${serviceId}/seen`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
    } catch (error) {
      console.error('Error marking service as seen:', error)
    }
  }

  const nextService = () => {
    setCurrentIndex(prev => (prev + 1) % services.length)
    setTimeLeft(30)
  }

  const prevService = () => {
    setCurrentIndex(prev => (prev - 1 + services.length) % services.length)
    setTimeLeft(30)
  }

  const togglePlay = () => {
    setIsPlaying(!isPlaying)
  }

  const getServiceTypeLabel = (serviceType: string) => {
    const labels = {
      'odbiór_przyłącza': 'Odbiór przyłącza',
      'częściowy_odbiór_przyłącza': 'Częściowy odbiór przyłącza',
      'wymiana_wodomierza': 'Wymiana wodomierza',
      'usunięcie_awarii': 'Usunięcie awarii',
      'zamknięcie_przyłącza': 'Zamknięcie przyłącza',
      'kontrola_stacji_uzdatniania_wody': 'Kontrola SUW',
      'zlecenie_wewnętrzne': 'Zlecenie wewnętrzne',
      'skoordynacja': 'Koordynacja',
      'inne': 'Inne'
    }
    return labels[serviceType as keyof typeof labels] || serviceType
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      'utworzone': 'Utworzone',
      'zaplanowane': 'Zaplanowane',
      'zrealizowane': 'Zrealizowane'
    }
    return labels[status as keyof typeof labels] || status
  }

  const getPriorityLabel = (priority: string) => {
    const labels = {
      'niski': 'Niski',
      'średni': 'Średni',
      'wysoki': 'Wysoki'
    }
    return labels[priority as keyof typeof labels] || priority
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'wysoki': return 'text-red-600 bg-red-100'
      case 'średni': return 'text-yellow-600 bg-yellow-100'
      case 'niski': return 'text-green-600 bg-green-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  if (pairingFailed) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-2xl text-center text-white px-8">
          <h1 className="text-3xl font-bold mb-4">Nie udało się sparować urządzenia</h1>
          <p className="text-red-300 text-xl mb-2">{error || 'Kod parowania jest nieprawidłowy lub wygasł.'}</p>
          <p className="text-slate-200 text-base">Wygeneruj nowy kod i otwórz ponownie link z parametrem pairing_code.</p>
        </div>
      </div>
    )
  }

  if (!hasAnyAuth) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-2xl text-center text-white px-8">
          <h1 className="text-4xl font-bold mb-4">Ekran kiosk nie jest sparowany</h1>
          <p className="text-slate-200 text-xl mb-6">
            Administrator musi wygenerować jednorazowy kod parowania i otworzyć link:
          </p>
          <code className="block bg-black/50 border border-slate-700 rounded-lg p-4 text-left text-base">
            https://nexus.macra.pl/services-display?pairing_code=TWÓJ_KOD
          </code>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-slate-100 mb-4"></div>
          <h2 className="text-3xl font-bold text-white mb-2">Ładowanie danych zleceń...</h2>
          <p className="text-slate-200 text-lg">Proszę czekać</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Błąd</h2>
          <p className="text-red-300 text-xl">{error}</p>
          <button
            onClick={loadServices}
            className="mt-6 px-8 py-4 bg-slate-100 text-slate-900 rounded-lg text-lg font-semibold hover:bg-white"
          >
            Spróbuj ponownie
          </button>
        </div>
      </div>
    )
  }

  if (services.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-[clamp(1rem,3vw,3rem)]">
        <div className="relative z-10 text-center w-full max-w-[min(90vw,60rem)]">
          <div className="mb-[clamp(1.5rem,3vw,3rem)]">
            <img
              src="/mnexus_logo 500 x 200.png"
              alt="MNexus Logo"
              className="mx-auto h-[clamp(4rem,7vw,9rem)] w-auto opacity-75"
            />
          </div>

          <div className="mb-8">
            <h1 className="text-[clamp(2.5rem,8vw,8rem)] font-bold text-white mb-6 leading-none">
              BRAK ZADAŃ
            </h1>
            <h2 className="kiosk-h font-semibold text-slate-100 mb-4">
              Wszystkie zlecenia zostały zrealizowane
            </h2>
            <p className="kiosk-text-sm text-slate-300 font-medium">
              Oczekiwanie na nowe zlecenia
            </p>
          </div>

          <div className="kiosk-text-xs text-slate-300">
            <p>Strona automatycznie odświeża się co 2 minuty</p>
            <p>Ostatnia aktualizacja: {new Date().toLocaleTimeString('pl-PL')}</p>
          </div>
        </div>
      </div>
    )
  }

  const currentService = services[currentIndex]

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Nagłówek */}
        <div className="flex flex-wrap gap-2 justify-between items-center p-[clamp(0.5rem,1.2vw,1.25rem)] border-b border-slate-700 bg-slate-950/90">
          <div className="flex flex-wrap items-center gap-[clamp(0.5rem,1vw,1rem)]">
            {!isKioskMode && (
              <Link
                to="/services"
                className="bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700 rounded px-3 py-1 kiosk-text-xs transition-colors"
              >
                ← Powrót do listy zleceń
              </Link>
            )}
            <div className="bg-slate-800 rounded px-3 py-1 border border-slate-700">
              <h1 className="kiosk-text-sm font-semibold text-slate-100">Ekran zleceń</h1>
              {isKioskMode && <p className="kiosk-label text-slate-400 tracking-wide">TRYB KIOSK</p>}
            </div>
            <div className="bg-slate-800 rounded px-3 py-1 border border-slate-700">
              <p className="text-slate-300 kiosk-text-xs font-medium">
                Zlecenie {currentIndex + 1} z {services.length}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-[clamp(0.5rem,1vw,1rem)]">
            {/* Kontrolki nawigacji */}
            <div className="bg-slate-800 rounded p-1 flex space-x-1 border border-slate-700 opacity-80">
              <button
                onClick={prevService}
                className="p-[clamp(0.4rem,0.8vw,0.9rem)] text-slate-300 hover:text-white rounded hover:bg-slate-700 transition-colors"
                title="Poprzedni serwis"
              >
                <svg className="w-[clamp(1.25rem,1.8vw,2.25rem)] h-[clamp(1.25rem,1.8vw,2.25rem)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
                </svg>
              </button>

              <button
                onClick={togglePlay}
                className="p-[clamp(0.4rem,0.8vw,0.9rem)] text-slate-300 hover:text-white rounded hover:bg-slate-700 transition-colors"
                title={isPlaying ? "Pauza" : "Odtwórz"}
              >
                {isPlaying ? (
                  <svg className="w-[clamp(1.25rem,1.8vw,2.25rem)] h-[clamp(1.25rem,1.8vw,2.25rem)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                ) : (
                  <svg className="w-[clamp(1.25rem,1.8vw,2.25rem)] h-[clamp(1.25rem,1.8vw,2.25rem)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l.707.707A1 1 0 0012.414 11H15m-1 4h1.586a1 1 0 01.707.293l.707.707A1 1 0 0017.414 11H21M7 7a4 4 0 018 0m-4-2v2"/>
                  </svg>
                )}
              </button>

              <button
                onClick={nextService}
                className="p-[clamp(0.4rem,0.8vw,0.9rem)] text-slate-300 hover:text-white rounded hover:bg-slate-700 transition-colors"
                title="Następny serwis"
              >
                <svg className="w-[clamp(1.25rem,1.8vw,2.25rem)] h-[clamp(1.25rem,1.8vw,2.25rem)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                </svg>
              </button>
            </div>

            {/* Timer */}
            <div className="bg-slate-800 rounded px-3 py-1 border border-slate-700 min-w-[clamp(140px,12vw,260px)] text-center opacity-80">
              <div className="text-slate-200 font-mono kiosk-text-xs font-semibold">
                Przejście za <span className={timeLeft <= 5 ? 'text-red-300' : 'text-emerald-300'}>{timeLeft}s</span>
              </div>
            </div>

            {/* Odświeżenie */}
            <button
              onClick={loadServices}
              className="bg-slate-800 text-slate-300 p-[clamp(0.4rem,0.8vw,0.9rem)] rounded border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors opacity-80"
              title="Odśwież dane"
            >
              <svg className="w-[clamp(1.1rem,1.5vw,2rem)] h-[clamp(1.1rem,1.5vw,2rem)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Główna sekcja serwisu */}
        <div className="flex-1 flex items-center justify-center p-[clamp(0.75rem,2vw,2.5rem)]">
          <div className="w-full">
            <div className="bg-slate-800 rounded-2xl p-[clamp(1rem,2vw,2.5rem)] border border-slate-700 shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-[clamp(1rem,2vw,2.5rem)]">
                {/* Lewa kolumna - Podstawowe informacje */}
                <div className="space-y-[clamp(1rem,1.6vw,1.75rem)]">
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="kiosk-title font-bold text-white pr-4">
                      {getServiceTypeLabel(currentService.service_type)}
                    </h2>
                    <span className={`shrink-0 px-5 py-3 rounded-lg kiosk-text-sm font-semibold ${getPriorityColor(currentService.priority)}`}>
                      {getPriorityLabel(currentService.priority)} priorytet
                    </span>
                  </div>

                  {currentService.description && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h font-semibold text-white mb-4">Opis prac do wykonania</h3>
                      <p className="text-white kiosk-text leading-snug whitespace-pre-wrap font-medium">
                        {currentService.description}
                      </p>
                    </div>
                  )}

                  {(currentService.contact_person_name || currentService.contact_phone) && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h-sm font-semibold text-white mb-4">Kontakt</h3>
                      <div className="space-y-2">
                        {currentService.contact_person_name && (
                          <p className="text-white kiosk-text-sm">
                            {currentService.contact_person_name}
                          </p>
                        )}
                        {currentService.contact_phone && (
                          <p className="text-emerald-300 kiosk-text-sm font-semibold tracking-wide">
                            tel. {currentService.contact_phone}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {currentService.related_issue_id && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h-sm font-semibold text-white mb-4">Powiązane zgłoszenie</h3>
                      <p className="text-white kiosk-text-sm font-mono">{currentService.related_issue_id}</p>
                    </div>
                  )}
                </div>

                {/* Prawa kolumna - Szczegóły pracy */}
                <div className="space-y-[clamp(1rem,1.6vw,1.75rem)]">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.65rem,1.1vw,1.25rem)] border border-slate-700">
                      <h3 className="kiosk-label font-semibold text-slate-300 mb-1 uppercase tracking-wide">Numer serwisu</h3>
                      <p className="kiosk-text font-bold text-white tracking-wide">{currentService.id}</p>
                    </div>

                    <div className="bg-slate-800 rounded-lg p-[clamp(0.65rem,1.1vw,1.25rem)] border border-slate-700">
                      <h3 className="kiosk-label font-semibold text-slate-300 mb-1 uppercase tracking-wide">Status</h3>
                      <p className="kiosk-text-sm font-bold text-amber-300">
                        {getStatusLabel(currentService.status)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                    <h3 className="kiosk-h font-semibold text-white mb-4">Lokalizacja</h3>
                    <div className="space-y-2">
                      <p className="text-white kiosk-text font-semibold leading-snug">
                        {currentService.street_address}
                      </p>
                      {currentService.location && (
                        <p className="text-slate-200 kiosk-text-sm">
                          {currentService.location}
                        </p>
                      )}
                      {(currentService.latitude && currentService.longitude) && (
                        <p className="kiosk-text-xs text-slate-300">
                          Koordynaty: {currentService.latitude}, {currentService.longitude}
                        </p>
                      )}
                      {currentService.planned_date && (
                        <p className="kiosk-text-xs text-orange-300 mt-2 font-medium">
                          Planowana data realizacji: {format(new Date(currentService.planned_date), 'dd.MM.yyyy', { locale: pl })}
                        </p>
                      )}
                    </div>
                  </div>

                  {currentService.additional_tools && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h-sm font-semibold text-white mb-4">Potrzebne narzędzia / wyposażenie</h3>
                      <p className="text-white kiosk-text-sm leading-relaxed whitespace-pre-wrap">
                        {currentService.additional_tools}
                      </p>
                    </div>
                  )}

                  {/* Zdjęcia */}
                  {currentService.images && currentService.images.length > 0 && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h-sm font-semibold text-white mb-4">Zdjęcia serwisowe</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {currentService.images.map((img) => (
                          <div key={img.id} className="bg-slate-700 rounded-lg overflow-hidden border border-slate-600">
                            <img
                              src={`/${img.file_path}`}
                              alt={img.original_filename}
                              className="w-full h-[clamp(8rem,12vw,18rem)] object-cover rounded-lg"
                            />
                            <div className="p-3">
                              <p className="kiosk-text-xs text-white truncate">{img.original_filename}</p>
                              {img.description && (
                                <p className="kiosk-label text-slate-300">{img.description}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Historia statusów - pokaż ostatnie 3 wpisy */}
                  {currentService.status_history && currentService.status_history.length > 0 && (
                    <div className="bg-slate-800 rounded-lg p-[clamp(0.85rem,1.5vw,1.75rem)] border border-slate-700">
                      <h3 className="kiosk-h-sm font-semibold text-white mb-4">Historia zmian statusu</h3>
                      <div className="space-y-3">
                        {currentService.status_history.slice(-3).map((h) => (
                          <div key={h.id} className="bg-slate-700 rounded p-4 border border-slate-600">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="kiosk-text-xs text-white">
                                  {h.old_status ?
                                    `Zmieniono z ${getStatusLabel(h.old_status)} na ${getStatusLabel(h.new_status)}` :
                                    `Utworzono: ${getStatusLabel(h.new_status)}`
                                  }
                                </p>
                                <p className="kiosk-label text-slate-300 mt-1">
                                  przez {h.changed_by_name} • {format(new Date(h.changed_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                                </p>
                              </div>
                            </div>
                            {h.planning_date && (
                              <p className="kiosk-label text-orange-300 mt-1">
                                Zaplanowano na: {format(new Date(h.planning_date), 'dd.MM.yyyy', { locale: pl })}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stopka */}
        <div className="p-[clamp(0.5rem,1vw,1.25rem)] text-center">
          <p className="text-slate-300 kiosk-text-sm">
            Automatyczna prezentacja • Przejście co 30 sekund
          </p>
        </div>
      </div>
    </div>
  )
}
